import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { authService, type User, type AuthTokens } from '@/services/authService'

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterCredentials {
  name: string
  email: string
  password: string
  confirmPassword: string
  phone?: string
}

interface AuthContextType {
  user: User | null
  tokens: AuthTokens | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  login: (credentials: LoginCredentials) => Promise<void>
  register: (credentials: RegisterCredentials) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  clearError: () => void
  updateUser: (userData: Partial<User>) => void
  isAdmin: () => boolean
  isInstructor: () => boolean
  isStudent: () => boolean
  isSuperAdmin: () => boolean
  isVerified: () => boolean
  hasPermission: (code: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Helper to normalize user data from backend (snake_case to camelCase)
  const normalizeUser = (userData: any): User => {
    if (!userData) return userData
    return {
      ...userData,
      name: userData.name || userData.full_name || '',
      birthDate: userData.birthDate || userData.birth_date || undefined,
      emailVerifiedAt: userData.emailVerifiedAt || userData.email_verified_at || undefined,
    }
  }

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedTokens = localStorage.getItem('tokens')
    const storedUser = localStorage.getItem('user')

    if (storedTokens && storedUser) {
      try {
        const parsedTokens = JSON.parse(storedTokens)
        const parsedUser = JSON.parse(storedUser)
        setTokens(parsedTokens)
        setUser(normalizeUser(parsedUser))
      } catch {
        localStorage.removeItem('tokens')
        localStorage.removeItem('user')
        localStorage.removeItem('authToken')
        setTokens(null)
        setUser(null)
      }
    }
    setIsLoading(false)
  }, [])

  // Listen to 'auth-logout' event from api.ts interceptor (401 response)
  useEffect(() => {
    const handleExternalLogout = () => {
      setUser(null)
      setTokens(null)
      setError(null)
    }
    window.addEventListener('auth-logout', handleExternalLogout)
    return () => window.removeEventListener('auth-logout', handleExternalLogout)
  }, [])

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authService.login(credentials)

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Login failed')
      }

      const { user: loggedInUser, tokens: authTokens } = response.data

      if (!loggedInUser) {
        throw new Error('Invalid response from server')
      }

      // Handle TOTP flow — user must verify authenticator code before getting tokens
      // Note: API interceptor converts snake_case to camelCase (requires_totp → requiresTotp)
      if ((response.data as any).requires_totp || (response.data as any).requiresTotp ||
          (response.data as any).requires_totp_setup || (response.data as any).requiresTotpSetup) {
        localStorage.setItem('user', JSON.stringify(loggedInUser))
        localStorage.setItem('partialToken', (response.data as any).partial_token || (response.data as any).partialToken || '')
        localStorage.setItem('totpRequiresSetup',
          ((response.data as any).requires_totp_setup || (response.data as any).requiresTotpSetup) ? 'true' : 'false')

        setUser(normalizeUser(loggedInUser))
        setError(null)

        // Redirect to TOTP verification page
        window.location.href = '/verify-totp'
        return
      }

      if (!authTokens) {
        throw new Error('Invalid response from server')
      }

      localStorage.setItem('user', JSON.stringify(loggedInUser))
      localStorage.setItem('tokens', JSON.stringify(authTokens))
      localStorage.setItem('authToken', authTokens.accessToken)

      setUser(normalizeUser(loggedInUser))
      setTokens(authTokens)
      setError(null)

      // Dispatch custom event for auth state change
      window.dispatchEvent(new Event('auth-login'))
    } catch (err: unknown) {
      let errorMessage = 'Login failed'

      if (err instanceof Error) {
        errorMessage = err.message || 'Login failed'
      } else if (typeof err === 'object' && err !== null) {
        const errorObj = err as { message?: string; error?: { message?: string } }
        errorMessage = errorObj.message || errorObj.error?.message || 'Login failed'
      }

      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authService.register({
        email: credentials.email,
        password: credentials.password,
        name: credentials.name,
        phone: credentials.phone,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Registration failed')
      }

      const { user: registeredUser, tokens: authTokens } = response.data

      if (!registeredUser || !authTokens) {
        throw new Error('Invalid response from server')
      }

      localStorage.setItem('user', JSON.stringify(registeredUser))
      localStorage.setItem('tokens', JSON.stringify(authTokens))
      localStorage.setItem('authToken', authTokens.accessToken)

      setUser(normalizeUser(registeredUser))
      setTokens(authTokens)
      setError(null)

      // Dispatch custom event for auth state change
      window.dispatchEvent(new Event('auth-login'))
    } catch (err: unknown) {
      let errorMessage = 'Registration failed'

      if (err instanceof Error) {
        errorMessage = err.message || 'Registration failed'
      } else if (typeof err === 'object' && err !== null) {
        const errorObj = err as { message?: string; error?: { message?: string } }
        errorMessage = errorObj.message || errorObj.error?.message || 'Registration failed'
      }

      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authService.logout()
    } catch (err) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', err)
    }

    // Clear storage
    localStorage.removeItem('tokens')
    localStorage.removeItem('user')
    localStorage.removeItem('authToken')

    // Update state
    setUser(null)
    setTokens(null)
    setError(null)

    // Dispatch logout event for any external listeners
    window.dispatchEvent(new Event('auth-logout'))
  }

  const refreshToken = async (): Promise<void> => {
    if (!tokens) {
      throw new Error('No tokens available')
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1'
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error('Failed to refresh token')
      }

      localStorage.setItem('tokens', JSON.stringify(data.tokens))
      setTokens(data.tokens)
    } catch (err) {
      // If refresh fails, logout
      await logout()
      throw err
    }
  }

  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  const isAdmin = useCallback((): boolean => {
    return user?.role === 'admin'
  }, [user])

  const isInstructor = useCallback((): boolean => {
    return user?.role === 'instructor'
  }, [user])

  const isStudent = useCallback((): boolean => {
    return user?.role === 'student'
  }, [user])

  const isSuperAdmin = useCallback((): boolean => {
    return user?.role === 'super_admin'
  }, [user])

  const isVerified = useCallback((): boolean => {
    if (!user) return false
    const rec = user as unknown as Record<string, unknown>
    const v =
      (rec.emailVerifiedAt as string | null | undefined) ??
      (rec.email_verified_at as string | null | undefined)
    return v != null && v !== ''
  }, [user])

  const hasPermission = useCallback((code: string): boolean => {
    if (!user) return false
    if (user.role === 'super_admin') return true
    return user.permissions?.includes(code) ?? false
  }, [user])

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null
      const updated = normalizeUser({ ...prev, ...userData })
      localStorage.setItem('user', JSON.stringify(updated))
      return updated
    })
  }, [])

  const contextValue: AuthContextType = {
    user,
    tokens,
    isLoading,
    isAuthenticated: !!user && !!tokens,
    error,
    login,
    register,
    logout,
    refreshToken,
    clearError,
    updateUser,
    isAdmin,
    isInstructor,
    isStudent,
    isSuperAdmin,
    isVerified,
    hasPermission,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
